<template>
    <div>
        <ResultBar :message="reportMessage" :content="getFormattedDebriefReportCriteria" />
    </div>
</template>

<script setup>
import ResultBar from '../ResultBar.vue';

import { computed } from 'vue';
import { storeToRefs } from 'pinia';

import { reportStore, incidentStore, applianceStore, userStore, themeStore, sourceStore } from '@/store/global-stores';
import { getAllIncidentCategories } from '../../../incident/constants';
import { getAllOutcomeTypes } from '../../../learning-outcome/constants';
import { useLabels } from '@/composables/LabelComposable';

const { users } = storeToRefs(userStore);
const { debriefCriteria } = storeToRefs(reportStore);
const { flatIncidentTypes } = storeToRefs(incidentStore);
const { appliances } = storeToRefs(applianceStore);
const { flatThemeData } = storeToRefs(themeStore);
const { flatSources } = storeToRefs(sourceStore);
const { label } = useLabels();

const props = defineProps({
    totalRecords: {
        type: Number,
        default: 0,
    },
});

const reportMessage = computed(() => {
    return props.totalRecords > 0
        ? `${props.totalRecords} ${label('Debriefs').toLowerCase()}`
        : `no any ${label('Debriefs').toLowerCase()}`;
});

const getFormattedDebriefReportCriteria = computed(() => {
    const activeCriterias = {
        'Incident No': debriefCriteria.value.incident_number ?? null,
        'Incident Category': getExistingEntities('category') ?? null,
        'Incident Type': getExistingEntities('incident_type') ?? null,
        'Learning Identified': debriefCriteria.value.learning === 1 ? 'Yes' : debriefCriteria.value.learning === 0 ? 'No' : 'Both',
        Appliance: getExistingEntities('appliance') ?? null,
        Author: getExistingEntities('author') ?? null,
        Themes: getExistingEntities('theme') ?? null,
        Source: getExistingEntities('source') ?? null,
        'Outcome Types': getExistingEntities('outcome_type') ?? null,
        'Incident Date From': debriefCriteria.value.incident_date_from ?? null,
        'Incident Date To': debriefCriteria.value.incident_date_to ?? null,
    };
    const filteredObject = Object.fromEntries(Object.entries(activeCriterias).filter(([key, value]) => key !== null && value !== null));
    return filteredObject;
});

// For the banner result section of selected criterias
const getEntitiesBasedOnTitle = (entity) => {
    switch (entity) {
        case 'incident_type':
            return flatIncidentTypes.value;
        case 'category':
            return getAllIncidentCategories();
        case 'appliance':
            return appliances.value;
        case 'author':
            return users.value;
        case 'theme':
            return flatThemeData.value;
        case 'source':
            return flatSources.value;
        case 'outcome_type':
            return getAllOutcomeTypes();
    }
};

const getCriteriaEntryBasedOnEntity = (entity) => {
    switch (entity) {
        case 'incident_type':
            return 'incident_type_id';
        case 'category':
            return 'category';
        case 'appliance':
            return 'appliance_id';
        case 'author':
            return 'author_id';
        case 'theme':
            return 'theme_id';
        case 'source':
            return 'source_id';
        case 'outcome_type':
            return 'outcome_type_id';
    }
};

const getExistingEntities = (entity) => {
    let entityString = null;
    const criteriaEntry = getCriteriaEntryBasedOnEntity(entity);
    const existingCriteria = debriefCriteria.value[criteriaEntry];
    if (existingCriteria?.length) {
        const allEntities = getEntitiesBasedOnTitle(entity);
        entityString = existingCriteria
            .map((entityId) => {
                const currentEntity = allEntities?.find((entity) => entity.id === entityId);
                return (entity === 'users_assigned' ? currentEntity?.fullname : currentEntity?.name) ?? null;
            })
            ?.join(', ');
    }
    return entityString;
};
</script>
